import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

export type Message = {
    id: string;
    author: string;
    message: string;
    severity?: 'info' | 'warn' | 'error' | 'success';
    _id: string;
};

const MAX_MESSAGES = 100;

class WebSocketService {
    private client: WebSocket | null = null;
    private url: string;
    private connectedPromise: Promise<void> | null = null;
    private reconnectInterval = 1000;
    private listeners: Set<(msg: Message) => void> = new Set();
    private token: string | null = null;
    private messageQueue: any[] = [];
    private subscribedTopics: Map<string, number> = new Map(); // topic -> count

    constructor(url: string) {
        this.url = url;
        this.token = localStorage.getItem("token");
        this.connect();

        // Watch for token changes
        window.addEventListener("storage", this.handleStorageChange);
    }

    private handleStorageChange = (event: StorageEvent) => {
        if (event.key === "token" && event.newValue !== this.token) {
            console.info("Token changed, reconnecting WebSocket...");
            this.token = event.newValue;
            this.reconnect();
        }
    };

    private connect() {
        const token = this.token || localStorage.getItem("token");
        if (!token) {
            console.error("No token found for WebSocket connection");
            return;
        }

        this.client = new WebSocket(`${this.url}?token=${token}`);

        this.connectedPromise = new Promise<void>((resolve, reject) => {
            this.client!.onopen = () => {
                console.info("WebSocket connected");
                resolve();
                this.flushQueue();
                this.resubscribeTopics(); // resubscribe to all topics after reconnect
            };
            this.client!.onerror = (err) => {
                console.error("WebSocket error", err);
                reject(err);
            };
        });

        this.client.onmessage = (evt) => {
            try {
                const msg: Message = JSON.parse(evt.data);
                this.listeners.forEach((fn) => fn(msg));
            } catch (err) {
                console.error("Failed to parse WebSocket message", err);
            }
        };

        this.client.onclose = () => {
            console.warn("WebSocket closed, attempting reconnect...");
            setTimeout(() => this.connect(), this.reconnectInterval);
        };
    }

    private reconnect() {
        if (this.client) {
            this.client.close();
        }
    }

    private flushQueue() {
        while (this.messageQueue.length > 0 && this.client?.readyState === WebSocket.OPEN) {
            const msg = this.messageQueue.shift();
            try {
                this.client!.send(JSON.stringify(msg));
            } catch (err) {
                console.error("Failed to send queued message", err);
                this.messageQueue.unshift(msg);
                break;
            }
        }
    }

    private async resubscribeTopics() {
        for (const topic of this.subscribedTopics.keys()) {
            await this.send({ action: "subscribe", topic });
        }
    }

    async send(data: any) {
        try {
            await this.connectedPromise;
            if (this.client?.readyState === WebSocket.OPEN) {
                this.client.send(JSON.stringify(data));
            } else {
                console.warn("WebSocket not open, queueing message");
                this.messageQueue.push(data);
            }
        } catch (err) {
            console.warn("WebSocket not ready, queueing message", err);
            this.messageQueue.push(data);
        }
    }

    subscribe(listener: (msg: Message) => void, topic?: string) {
        this.listeners.add(listener);
        if (topic) {
            const count = this.subscribedTopics.get(topic) || 0;
            if (count === 0) {
                this.send({ action: "subscribe", topic });
            }
            this.subscribedTopics.set(topic, count + 1);
        }
    }

    unsubscribe(listener: (msg: Message) => void, topic?: string) {
        this.listeners.delete(listener);
        if (topic) {
            const count = this.subscribedTopics.get(topic);
            if (count) {
                if (count === 1) {
                    this.send({ action: "unsubscribe", topic });
                    this.subscribedTopics.delete(topic);
                } else {
                    this.subscribedTopics.set(topic, count - 1);
                }
            }
        }
    }
}

export const wsService = new WebSocketService(process.env.REACT_APP_BACKEND_WEBSOCKET!);

export const websocketApi = createApi({
    reducerPath: "websocketApi",
    baseQuery: fakeBaseQuery(),
    endpoints: (build) => ({
        sendToast: build.mutation<void, { message: string; author?: string; topic: string; severity: string; _id?: string; id?: string; }>({
            async queryFn(arg) {
                await wsService.send(arg);
                return { data: undefined };
            },
        }),
        channel: build.query<{ messages: Message[] }, string>({
            queryFn: () => ({ data: { messages: [] } }),
            async onCacheEntryAdded(topic, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                await cacheDataLoaded;

                const listener = (msg: Message) => {
                    updateCachedData((draft) => {
                        draft.messages.push(msg);
                        if (draft.messages.length > MAX_MESSAGES) draft.messages.shift();
                    });
                };

                wsService.subscribe(listener, topic);

                await cacheEntryRemoved;

                wsService.unsubscribe(listener, topic);
            },
        }),
    }),
});

export const { useSendToastMutation, useChannelQuery } = websocketApi;
